<template>
	<AssemblyLayout
		:assembly="assembly"
		:subtitle="election.title"
		:return-url="route('association.assemblies.elections.event-hosts.index', assembly.id)"
		:return-label="trans('assemblies.elections.event-hosts.title')"
	>
		<template v-if="election.open" #actions>
			<template v-if="election.active">
				<div class="flex items-center gap-2 text-green-600 uppercase animate-pulse">
					<i class="fa-solid fa-circle"/>
					{{ trans('general.on_air') }}
				</div>
				<PrimaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-lock-open"/>
				</PrimaryButton>
			</template>
			<template v-else>
				<PrimaryButton :color="Color.Green" @click.stop="ratify">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('assemblies.elections.ratify') }}
				</PrimaryButton>
				<PrimaryButton :color="Color.Blue" @click.stop="toggle">
					<i class="fa-solid fa-lock"/>
				</PrimaryButton>
			</template>
		</template>
		<Card :title="`${trans('assemblies.seats')}: ${election.limit}`">
			<template v-if="canRatify">
				<List>
					<ElectionCandidateComponent v-for="electionCandidate in election.candidates" :key="`election-candidate-${electionCandidate.id}`" :title="electionCandidate.candidate.name" :candidate="electionCandidate" :selected="selected" :election="election" :limit="election.limit"/>
				</List>
				<template v-if="election.closed">
					<Alert :color="Color.Green">{{ trans('assemblies.elections.ratified') }}</Alert>
					<LabeledObject>
						<template #label>{{ trans('assemblies.elections.ratification') }}</template>
						<p>{{ election.ratification }}</p>
					</LabeledObject>
				</template>
				<template v-else>
					<LabeledObject>
						<template #label>{{ trans('general.search') }}</template>
						<input v-model="filter" type="text">
					</LabeledObject>
					<Card v-for="college in filteredColleges" :key="`filtered-colleges-${college.id}`" class="hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer" @click.stop="storeCandidate(college.id)">
						{{ college.name }}
					</Card>
					<LabeledObject required>
						<template #label>{{ trans('assemblies.elections.ratification') }}</template>
						<textarea v-model="election.ratification"/>
					</LabeledObject>
				</template>
			</template>
			<Alert v-else :color="Color.Yellow">{{ trans('enrollments.accreditation.no_permissions') }}</Alert>
		</Card>
	</AssemblyLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {router} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import {useEchoModel} from '@laravel/echo-vue'
import Alert from '@/Components/UI/Alerts/Alert.vue'
import {type College} from '@/Types/Settings/College'
import {type Election} from '@/Types/Elections/Election'
import AssemblyLayout from '@/Layouts/AssemblyLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type ElectionVote} from '@/Types/Elections/ElectionVote'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {computed, type ComputedRef, onMounted, type Ref, ref} from 'vue'
import {type ElectionCandidate} from '@/Types/Elections/ElectionCandidate'
import ElectionCandidateComponent from '@/Components/Elections/ElectionCandidate.vue'
import {type EventHostElection} from '@/Types/Elections/Assemblies/EventHostElection'

const props = defineProps<{
	assembly:Assembly,
	canRatify:boolean,
	selected:Array<number>,
	colleges:Array<College>,
	election:EventHostElection,
}>()

const filter:Ref<string> = ref('')
const working:Ref<boolean> = ref(false)
const assembly:Ref<Assembly> = ref(props.assembly)
const selected:Ref<Array<number>> = ref(props.selected)
const colleges:Ref<Array<College>> = ref(props.colleges)
const election:Ref<EventHostElection> = ref(props.election)

const filteredColleges:ComputedRef<Array<College>> = computed(():Array<College> => {
	if(filter.value.length > 0){
		return colleges.value.filter((c:College) => c.name.match(new RegExp(filter.value, 'i')))
	}
	return []
})

function ratify():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.assemblies.elections.event-hosts.update', {assembly: assembly.value.id, election: election.value.id}), {
			selected: selected.value,
			ratification: election.value.ratification,
		}).then(({data}:{data:number|null|undefined}) => {
			showSuccess(trans('general.updated'))
			election.value.closed = true
			election.value.open = false
			if(data){
				router.get(route('association.assemblies.elections.event-hosts.show', data))
			}
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function toggle():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.assemblies.elections.event-hosts.toggle.update', {assembly: assembly.value.id, election: election.value.id})).then(({data}:{data:boolean}) => {
			showSuccess(trans('general.updated'))
			election.value.active = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function storeCandidate(collegeId:number):void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.post(route('association.assemblies.elections.event-hosts.candidates.store', {assembly: assembly.value.id, election: election.value.id}), {
			college_id: collegeId,
		}).then(({data}:{data:ElectionCandidate<EventHostElection, College>}) => {
			showSuccess(trans('general.updated'))
			election.value.candidates.push(data)
			filter.value = ''
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

onMounted(():void => {
	const assemblyEchoModel = useEchoModel('elections', election.value.id)
	assemblyEchoModel.channel().listen('VoteStored', (event:{vote:ElectionVote<Election<College>, College>}) => {
		if(event.vote.election_type === 'eventHostElection'){
			election.value.votes.push(event.vote)
		}
	})
})
</script>